import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Container from '../components/Container';
import Button from '../components/Button';
import Footer from '../components/Footer';
import CookieLogo from '../assets/cookie.png';

// Top Banner Component
const TopBanner = styled.div`
  width: 100%;
  background: linear-gradient(135deg, rgba(45, 108, 255, 0.9) 0%, rgba(110, 168, 255, 0.8) 100%);
  color: #fff;
  padding: 0.8rem 2rem;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(45, 108, 255, 0.3);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.7rem 1rem;
    font-size: 0.9rem;
  }
`;

const BannerLink = styled.a`
  color: #fff;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.2s ease;
  
  &:hover {
    text-decoration: underline;
    color: #f0f7ff;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0e1633 0%, #1e2a4a 100%);
  animation: ${fadeIn} 0.8s ease-out;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 2rem 0 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ReferrerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
`;

const ReferrerLogo = styled.img`
  width: 180px;
  height: 180px;
  object-fit: contain;
  margin-bottom: 1rem;
  padding: 6px;
  
  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    margin-bottom: 0.5rem;
  }
`;

const ReferrerName = styled.h1`
  font-size: 2.1rem;
  color: #6ea8ff;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 12px #2d6cffcc;
`;

const ReferrerChannel = styled.div`
  font-size: 1.1rem;
  color: #b6d0ff;
  margin-bottom: 0.5rem;
`;

const ReferrerDescription = styled.p`
  color: #b6d0ff;
  font-size: 1.1rem;
  max-width: 600px;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ErrorMessage = styled.div`
  background: rgba(44, 100, 255, 0.12);
  color:rgb(255, 45, 45);
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 0 8px #2d6cff44;
`;

const ReferrerLogosRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  margin-bottom: 1.2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    padding: 0 1rem;
  }
`;

const BigX = styled.div`
  font-size: 5rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 0 32px #fff, 0 0 16px #2d6cffcc, 0 0 8px #fff;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const ToolsHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const ToolsTitle = styled.h2`
  font-size: 2.5rem;
  color: #6ea8ff;
  font-weight: 900;
  margin-bottom: 1rem;
  text-shadow: 0 0 16px #2d6cffcc;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ToolsSubtitle = styled.p`
  font-size: 1.2rem;
  color: #b6d0ff;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 0 1rem;
  }
`;

const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ToolCard = styled(Link)`
  background: rgba(20, 30, 60, 0.65);
  border-radius: 24px;
  padding: 2.5rem;
  border: 2px solid #2d6cff44;
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 32px 0 rgba(34, 60, 120, 0.18);
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  display: block;
  
  &:hover {
    border-color: #2d6cff;
    box-shadow: 0 16px 48px 0 rgba(45, 108, 255, 0.4);
    transform: translateY(-8px);
    text-decoration: none;
    color: inherit;
  }
`;

const ToolIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ToolTitle = styled.h3`
  color: #fff;
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-align: center;
`;

const ToolDescription = styled.p`
  color: #b6d0ff;
  line-height: 1.6;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ToolFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
  color: #b6d0ff;
`;

const ToolFeature = styled.li`
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #2d6cff;
    font-weight: bold;
  }
`;

const ToolCTA = styled.div`
  text-align: center;
`;

const CTAButton = styled.div`
  background: linear-gradient(135deg, #2d6cff 0%, #6ea8ff 100%);
  color: #fff;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  display: inline-block;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(45, 108, 255, 0.4);
  }
`;

const PriceTag = styled.div`
  background: rgba(45, 108, 255, 0.15);
  color: #6ea8ff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  margin-bottom: 1rem;
  display: inline-block;
`;

function ReferralPage() {
  const { referrer } = useParams();
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch referrer info
    fetch(`/api/referrer/${referrer}`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setReferrerInfo(data.referrer);
        else setError('Referrer not found.');
      })
      .catch(() => setError('Error loading referrer info.'));
  }, [referrer]);

  if (error) {
    return (
      <PageWrapper>
        <TopBanner>
          Join our channels: <BannerLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</BannerLink>
        </TopBanner>
        <Container>
          <ErrorMessage>{error}</ErrorMessage>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <TopBanner>
        Join our channels: <BannerLink href="https://t.me/DeservedGatewayBot" target="_blank" rel="noopener noreferrer">@DeservedGatewayBot</BannerLink>
      </TopBanner>
      <ContentWrapper>
        <Container>
          {referrerInfo && (
            <ReferrerHeader>
              <ReferrerLogosRow>
                <ReferrerLogo src={CookieLogo} alt="Deserved Cookie Logo"/>
                <BigX>×</BigX>
                {referrerInfo.logo_url && <ReferrerLogo src={referrerInfo.logo_url} alt={referrerInfo.name} />}
              </ReferrerLogosRow>
              <ReferrerName>{referrerInfo.name}</ReferrerName>
              {referrerInfo.channel && <ReferrerChannel>{referrerInfo.channel}</ReferrerChannel>}
              {referrerInfo.description && <ReferrerDescription>{referrerInfo.description}</ReferrerDescription>}
            </ReferrerHeader>
          )}

          <ToolsHeader>
            <ToolsTitle>Professional Tools</ToolsTitle>
            <ToolsSubtitle>
              Cutting-edge automation and virtual solutions designed for professionals. 
              Choose the tool that fits your needs or get both for complete coverage.
            </ToolsSubtitle>
          </ToolsHeader>

          <ToolsGrid>
            <ToolCard to={`/ref/${referrer}/warmer`}>
              <ToolIcon>🚀</ToolIcon>
              <ToolTitle>DeservedWarmer</ToolTitle>
              <PriceTag>Starting at $60/month</PriceTag>
              <ToolDescription>
                Professional browser automation tool that builds trusted, verified accounts on 
                Amazon, eBay, Vinted and more with human-like browsing patterns.
              </ToolDescription>
              <ToolFeatures>
                <ToolFeature>Automated account warming</ToolFeature>
                <ToolFeature>Human-like behavior simulation</ToolFeature>
                <ToolFeature>Multi-platform support</ToolFeature>
                <ToolFeature>AdsPower integration</ToolFeature>
                <ToolFeature>Privacy-first local automation</ToolFeature>
              </ToolFeatures>
              <ToolCTA>
                <CTAButton>
                  Learn More & Purchase →
                </CTAButton>
              </ToolCTA>
            </ToolCard>

            <ToolCard to={`/ref/${referrer}/camera`}>
              <ToolIcon>📹</ToolIcon>
              <ToolTitle>DeservedCam</ToolTitle>
              <PriceTag>$300 Lifetime</PriceTag>
              <ToolDescription>
                Professional virtual webcam solution for identity verification. 
                Upload your ID photo and stream it with authentic camera effects.
              </ToolDescription>
              <ToolFeatures>
                <ToolFeature>Virtual webcam driver</ToolFeature>
                <ToolFeature>ID photo camera effects</ToolFeature>
                                  <ToolFeature>Natural camera behavior</ToolFeature>
                <ToolFeature>Universal compatibility</ToolFeature>
                <ToolFeature>Undetectable technology</ToolFeature>
              </ToolFeatures>
              <ToolCTA>
                <CTAButton>
                  Learn More & Purchase →
                </CTAButton>
              </ToolCTA>
            </ToolCard>
          </ToolsGrid>

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{ 
              background: 'rgba(20, 30, 60, 0.65)', 
              borderRadius: '18px', 
              padding: '2rem', 
              border: '1.5px solid #2d6cff44',
              backdrop: 'blur(8px)',
              boxShadow: '0 8px 32px 0 rgba(34, 60, 120, 0.18)'
            }}>
              <h3 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.5rem' }}>
                Need Help Choosing?
              </h3>
              <p style={{ color: '#b6d0ff', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                Contact our team on Telegram for personalized recommendations and support.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a 
                  href="https://t.me/deserved0" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    background: 'rgba(45, 108, 255, 0.15)',
                    color: '#6ea8ff',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                >
                  @deserved0
                </a>
                <a 
                  href="https://t.me/tomnookbank" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ 
                    background: 'rgba(45, 108, 255, 0.15)',
                    color: '#6ea8ff',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                >
                  @tomnookbank
                </a>
              </div>
            </div>
          </div>
        </Container>
      </ContentWrapper>
      <Footer />
    </PageWrapper>
  );
}

export default ReferralPage; 